import { z } from 'zod';
import { TickSchema } from './market';
import { OrderSchema } from './trading';
import { ChallengeSessionSchema } from './challenge';

/**
 * The wire protocol between the mobile app and the ws-gateway.
 * JSON text frames; every message is discriminated on `t`.
 */

/** Client → server. */
export const WsClientMessageSchema = z.discriminatedUnion('t', [
  z.object({ t: z.literal('sub'), symbols: z.array(z.string()).min(1).max(200) }),
  z.object({ t: z.literal('unsub'), symbols: z.array(z.string()).min(1).max(200) }),
  /** Attach a user identity to this connection to receive personal events. */
  z.object({ t: z.literal('auth'), token: z.string() }),
  z.object({ t: z.literal('ping') }),
]);
export type WsClientMessage = z.infer<typeof WsClientMessageSchema>;

/**
 * Personal push events, delivered on an authenticated connection.
 * order_update drives the fill toast (screen 19); challenge_update drives the
 * live risk banners (06/14) and pass/fail navigation (12/13).
 */
export const UserEventSchema = z.discriminatedUnion('type', [
  z.object({ type: z.literal('order_update'), order: OrderSchema }),
  z.object({
    type: z.literal('challenge_update'),
    /** Present so a terminal update can route to the result screen app-wide. */
    enrollmentId: z.string().optional(),
    session: ChallengeSessionSchema,
  }),
]);
export type UserEvent = z.infer<typeof UserEventSchema>;

/** Server → client. */
export const WsServerMessageSchema = z.discriminatedUnion('t', [
  /** A market tick for a subscribed symbol. */
  z.object({ t: z.literal('tick'), tick: TickSchema }),
  /**
   * Retained last-known ticks, sent immediately on subscribe so a screen is
   * never blank while waiting for the next live tick. `stale: true` when the
   * upstream feed is down — the app must render these as stale, never live.
   */
  z.object({ t: z.literal('snapshot'), ticks: z.array(TickSchema), stale: z.boolean() }),
  /** Upstream feed state — drives the app's live/reconnecting/stale banner. */
  z.object({ t: z.literal('feed'), state: z.enum(['live', 'reconnecting', 'closed']) }),
  /** A personal event for the authenticated user on this connection. */
  z.object({ t: z.literal('event'), event: UserEventSchema }),
  /** Auth acknowledgement. */
  z.object({ t: z.literal('authed'), ok: z.boolean() }),
  z.object({ t: z.literal('pong') }),
  z.object({ t: z.literal('error'), message: z.string() }),
]);
export type WsServerMessage = z.infer<typeof WsServerMessageSchema>;
